import axios, { type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

/**
 * REL-58: показ HTTP 402 (недостаточно средств на балансе компании) тостом.
 *
 * Списание платных модулей идёт в серверных/асинхронных потоках (olivia → InternalCreditsController,
 * батч-флаш диалогов) и в синхронных проверках кредитов AI-рассылок. 402 может прийти в любой
 * запрос, поэтому перехват вешаем на ВСЕ используемые axios-инстансы: и на default `axios`, и на
 * кастомный `api` из relevanter (создан через axios.create со своими interceptors — на него
 * перехват default-инстанса НЕ распространяется). Ошибку пробрасываем дальше — по-методная обработка
 * не ломается.
 */
let lastShownAt = 0;
let lastMessage = '';
const registeredInstances = new WeakSet<object>();

function handleRejected(error: any): Promise<never> {
  const status = error?.response?.status;
  const message: unknown = error?.response?.data?.message;
  if (status === 402 && typeof message === 'string' && message.includes('Недостаточно средств')) {
    const now = Date.now();
    // Дедуп: один и тот же текст не чаще раза в 3 секунды (несколько параллельных запросов).
    if (message !== lastMessage || now - lastShownAt > 3000) {
      lastShownAt = now;
      lastMessage = message;
      toast.error(message, { id: 'insufficient-balance', duration: 6000 });
    }
  }
  return Promise.reject(error);
}

/** Регистрирует перехват 402 на конкретном axios-инстансе (идемпотентно по инстансу). */
export function registerBalanceInterceptor(instance: AxiosInstance): void {
  if (registeredInstances.has(instance)) return;
  registeredInstances.add(instance);
  instance.interceptors.response.use(response => response, handleRejected);
}

/**
 * Регистрирует перехват на default-инстансе axios (общие вызовы apiService и сгенерированных
 * клиентов). Кастомные инстансы (например, relevanter `api`) регистрируются отдельно через
 * {@link registerBalanceInterceptor} в месте их создания.
 */
export function setupBalanceInterceptor(): void {
  registerBalanceInterceptor(axios);
}
